import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { Banknote, Calendar, Clock, Power, Save, Loader2, Info } from 'lucide-react';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = Array.from({ length: 24 }, (_, h) => h);

const fmtHour = (h) => {
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:00 ${period}`;
};

export default function AdminWithdrawalSettings() {
  const [form, setForm] = useState({
    withdrawalActive: true,
    withdrawalOffDay: 0,
    withdrawalStartHour: 0,
    withdrawalEndHour: 10
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);

  const fetchSettings = () => {
    setLoading(true);
    api.get('/settings/roi')
      .then(r => {
        const { withdrawalActive, withdrawalOffDay, withdrawalStartHour, withdrawalEndHour, updatedAt } = r.data;
        setForm({ withdrawalActive, withdrawalOffDay, withdrawalStartHour, withdrawalEndHour });
        setMeta({ updatedAt });
      })
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load settings'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      withdrawalActive: !!form.withdrawalActive,
      withdrawalOffDay: Number(form.withdrawalOffDay),
      withdrawalStartHour: Number(form.withdrawalStartHour),
      withdrawalEndHour: Number(form.withdrawalEndHour)
    };

    if (payload.withdrawalStartHour >= payload.withdrawalEndHour) {
      return toast.error('Start hour must be before end hour');
    }

    setSaving(true);
    try {
      const res = await api.put('/settings/roi', payload);
      toast.success(res.data.message);
      setMeta({ updatedAt: res.data.settings.updatedAt });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <h1 className="page-title">Withdrawal Settings</h1>
      <p className="page-subtitle">Control which day and time window users are allowed to submit withdrawal requests</p>

      {loading ? (
        <div className="loader-wrap"><div className="loader"></div></div>
      ) : (
        <div className="grid-2" style={{ gap: 24, alignItems: 'flex-start' }}>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Banknote size={18} /> Configuration
              </h3>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="checkbox"
                  id="withdrawalActive"
                  name="withdrawalActive"
                  checked={form.withdrawalActive}
                  onChange={handleChange}
                  style={{ width: 18, height: 18 }}
                />
                <label htmlFor="withdrawalActive" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Power size={14} /> Withdrawals Active
                </label>
              </div>

              {!form.withdrawalActive && (
                <div className="alert alert-warning" style={{ fontSize: '0.82rem', marginBottom: 16 }}>
                  Withdrawals are fully paused. No new withdrawal requests can be submitted by any user until this is re-enabled.
                </div>
              )}

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Calendar size={14} /> Weekly Off Day (no withdrawals)</label>
                <select name="withdrawalOffDay" className="form-control" value={form.withdrawalOffDay} onChange={handleChange}>
                  {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
                <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>Plan default: Sunday off, withdrawals Monday–Saturday.</small>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={14} /> Window Start (IST)</label>
                <select name="withdrawalStartHour" className="form-control" value={form.withdrawalStartHour} onChange={handleChange}>
                  {HOURS.map(h => <option key={h} value={h}>{fmtHour(h)}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={14} /> Window End (IST)</label>
                <select name="withdrawalEndHour" className="form-control" value={form.withdrawalEndHour} onChange={handleChange}>
                  {HOURS.map(h => <option key={h} value={h}>{fmtHour(h)}</option>)}
                </select>
                <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                  Users can submit requests from {fmtHour(Number(form.withdrawalStartHour))} up to (but not including) {fmtHour(Number(form.withdrawalEndHour))}, IST. Plan default: 12:00 AM – 10:00 AM.
                </small>
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={saving}>
                {saving ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Loader2 size={16} className="spin" /> Saving...</span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Save size={16} /> Save Settings</span>
                )}
              </button>

              {meta?.updatedAt && (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 10, textAlign: 'center' }}>
                  Last updated: {new Date(meta.updatedAt).toLocaleString('en-IN')}
                </p>
              )}
            </form>
          </div>

          <div className="card" style={{ background: 'rgba(var(--primary-rgb),0.05)' }}>
            <div className="card-header">
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Info size={18} /> How this works
              </h3>
            </div>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12, color: 'var(--text-light)', fontSize: '0.88rem' }}>
              <li>▸ These rules apply to <strong>new withdrawal requests</strong> only — approving/rejecting existing requests as admin is never blocked.</li>
              <li>▸ Times are treated as IST (Indian Standard Time), regardless of server timezone.</li>
              <li>▸ The off-day and time window are both enforced on the backend too, so this can't be bypassed even by calling the API directly.</li>
              <li>▸ The user's Withdraw page shows a live banner and disables the submit button whenever the window is closed.</li>
              <li>▸ Bank credit timing (e.g. "2:00 PM same day") is informational only — admin still manually approves each request.</li>
            </ul>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}